import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { Toast } from "../ui/Toast";
import axios from "axios";
import {
  processExcelFile,
  mapExcelRowToProjectInput,
  mapExcelRowToESGInput,
  mapExcelRowToEPCInput,
} from "../utils/excelProcessing";

const BulkProjectImporter = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [projectFolders, setProjectFolders] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentFolderId, setCurrentFolderId] = useState("root");
  const [breadcrumbs, setBreadcrumbs] = useState([
    { id: "root", name: "Drive" },
  ]);
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");

  const driveApiUrl =
    process.env.REACT_APP_DRIVE_API_URL || "http://localhost:8080";
  const apiRoot = process.env.REACT_APP_API_ROOT;
  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;
  const userID = localStorage.getItem("userID");
  const token = localStorage.getItem("token");

  useEffect(() => {
    checkAuthorization();
  }, []);

  const showCustomToast = (message, type, duration = 3000) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);

    if (type !== "loading") {
      setTimeout(() => {
        setShowToast(false);
      }, duration);
    }
  };

  const hideToast = () => {
    setShowToast(false);
  };

  const checkAuthorization = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${driveApiUrl}/api/drive/is-authorized`,
        {
          params: { user_id: userID },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsAuthorized(response.data.authorized);

      if (response.data.authorized) {
        setCurrentFolderId("root");
        setBreadcrumbs([{ id: "root", name: "Drive" }]);
        await fetchProjectFolders("root");
      }
    } catch (error) {
      console.error("Error checking authorization:", error);
      showCustomToast("Failed to check Google Drive authorization", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthClick = async () => {
    try {
      const response = await axios.get(`${driveApiUrl}/api/drive/auth-url`, {
        params: { user_id: userID },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const authWindow = window.open(
        response.data.auth_url,
        "googleAuth",
        "width=600,height=700"
      );

      const pollTimer = window.setInterval(() => {
        if (authWindow.closed) {
          window.clearInterval(pollTimer);
          setTimeout(checkAuthorization, 1000);
        }
      }, 500);
    } catch (error) {
      console.error("Error getting auth URL:", error);
      showCustomToast("Failed to initiate Google Drive authorization", "error");
    }
  };

  const fetchProjectFolders = useCallback(
    async (folderId) => {
      if (isFetchingFolders) return;

      setIsFetchingFolders(true);
      try {
        showCustomToast("Fetching folders from Google Drive...", "loading");
        setProjectFolders([]);

        setDebugInfo(`Fetching folders for ID: ${folderId}`);

        const response = await axios.get(`${driveApiUrl}/api/drive/folders`, {
          params: {
            user_id: userID,
            parent_id: folderId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const folders = response.data.folders || [];
        console.log(
          `Fetched ${folders.length} folders for parent ID: ${folderId}`
        );

        setDebugInfo(
          (prev) =>
            `${prev}\nReceived ${folders.length} folders\n${folders
              .map((f) => `${f.name} (${f.id})`)
              .join("\n")}`
        );

        setProjectFolders(folders);

        if (folders.length === 0) {
          showCustomToast("No folders found in this location.", "info");
        } else {
          showCustomToast(`Found ${folders.length} folders`, "success");
        }
      } catch (error) {
        console.error("Error fetching folders:", error);
        setDebugInfo((prev) => `${prev}\nError: ${error.message}`);
        showCustomToast("Failed to fetch folders", "error");
      } finally {
        setIsFetchingFolders(false);
      }
    },
    [driveApiUrl, token, userID, isFetchingFolders]
  );

  const navigateToFolder = (folder) => {
    if (isFetchingFolders) return;

    setSelectedProjects([]);

    setDebugInfo(`Navigating to: ${folder.name} (${folder.id})`);

    setBreadcrumbs((prevBreadcrumbs) => [
      ...prevBreadcrumbs,
      { id: folder.id, name: folder.name },
    ]);

    setCurrentFolderId(folder.id);

    fetchProjectFolders(folder.id);
  };

  const navigateToBreadcrumb = (index) => {
    if (isFetchingFolders) return;

    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);

    const targetFolder = newBreadcrumbs[index];
    setDebugInfo(
      `Navigating via breadcrumb to: ${targetFolder.name} (${targetFolder.id})`
    );

    setSelectedProjects([]);

    const folderId = newBreadcrumbs[index].id;

    setCurrentFolderId(folderId);

    fetchProjectFolders(folderId);
  };

  const toggleProjectSelection = (folderId) => {
    setSelectedProjects((prevSelected) => {
      if (prevSelected.includes(folderId)) {
        return prevSelected.filter((id) => id !== folderId);
      } else {
        return [...prevSelected, folderId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedProjects.length === projectFolders.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projectFolders.map((folder) => folder.id));
    }
  };

  const downloadExcelFile = async (fileId) => {
    try {
      const response = await axios.get(
        `${driveApiUrl}/api/drive/download-file`,
        {
          params: { user_id: userID, file_id: fileId },
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let fileName = "downloaded.xlsx";
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.*)/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      return new File([response.data], fileName, {
        type: response.headers["content-type"],
      });
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      throw new Error("Failed to download Excel file");
    }
  };

  const processFolder = async (folder) => {
    try {
      const folderContentResponse = await axios.get(
        `${driveApiUrl}/api/drive/folder-content`,
        {
          params: { user_id: userID, folder_id: folder.id },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const folderContent = folderContentResponse.data;

      if (!folderContent.excel_file) {
        throw new Error("No Excel file found in folder");
      }

      const excelFile = await downloadExcelFile(folderContent.excel_file.id);

      const excelData = await processExcelFile(excelFile);

      if (
        !excelData ||
        !excelData.jsonData ||
        excelData.jsonData.length === 0
      ) {
        throw new Error("Failed to extract data from Excel file");
      }

      showCustomToast(`Creating project from ${folder.name}...`, "loading");

      const projectInput = mapExcelRowToProjectInput(
        excelData.jsonData[0],
        userID
      );
      if (!projectInput.descriptiveName && projectInput.name) {
        projectInput.descriptiveName = projectInput.name;
      }

      const projectResponse = await axios.post(
        `${apiRootOperational}/api/projects`,
        projectInput,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!projectResponse.data || !projectResponse.data.id) {
        throw new Error("Failed to create project");
      }

      const projectId = projectResponse.data.id;

      try {
        const esgInput = mapExcelRowToESGInput(
          excelData.jsonData[0],
          projectId
        );
        await axios.post(`${apiRootOperational}/api/esg-data`, esgInput, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error(
          `Warning: Failed to create ESG data for project ${projectId}:`,
          error
        );
      }

      try {
        const epcInput = mapExcelRowToEPCInput(
          excelData.jsonData[0],
          projectId
        );
        await axios.post(`${apiRootOperational}/api/epc`, epcInput, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error(
          `Warning: Failed to create EPC data for project ${projectId}:`,
          error
        );
      }

      try {
        await axios.post(
          `${apiRoot}/api/project_assignment/`,
          [
            {
              user: parseInt(userID),
              project: parseInt(projectId),
            },
          ],
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (error) {
        console.error(
          `Warning: Failed to assign project ${projectId} to user:`,
          error
        );
      }

      for (const file of folderContent.other_files || []) {
        if (file.mime_type && file.mime_type.startsWith("image/")) {
          try {
            const imageResponse = await axios.get(
              `${driveApiUrl}/api/drive/download-file`,
              {
                params: { user_id: userID, file_id: file.id },
                responseType: "blob",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            const imageFile = new File([imageResponse.data], file.name, {
              type: file.mime_type,
            });

            const formData = new FormData();
            formData.append("image_file", imageFile);
            formData.append("project", projectId);

            await axios.post(`${apiRoot}/api/images/`, formData, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            });
          } catch (error) {
            console.error(
              `Warning: Failed to upload image ${file.name}:`,
              error
            );
          }
        }
      }

      return {
        success: true,
        projectId: projectId,
        message: `Project created successfully with ID: ${projectId}`,
      };
    } catch (error) {
      console.error(`Error processing folder ${folder.name}:`, error);
      return {
        success: false,
        message: error.message || "Failed to process project folder",
      };
    }
  };

  const processSelectedFolders = async () => {
    if (selectedProjects.length === 0) {
      showCustomToast(
        "Please select at least one project folder to import",
        "error"
      );
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: selectedProjects.length });
    showCustomToast(
      `Starting to process ${selectedProjects.length} projects...`,
      "loading"
    );

    try {
      const foldersToProcess = projectFolders.filter((folder) =>
        selectedProjects.includes(folder.id)
      );

      const initialStatus = {};
      selectedProjects.forEach((folderId) => {
        initialStatus[folderId] = "processing";
      });
      setProcessingStatus(initialStatus);

      let successCount = 0;
      let failCount = 0;
      const newStatus = { ...initialStatus };

      for (let i = 0; i < foldersToProcess.length; i++) {
        const folder = foldersToProcess[i];
        setProgress({ current: i + 1, total: selectedProjects.length });
        showCustomToast(
          `Processing project ${i + 1}/${selectedProjects.length}: ${
            folder.name
          }`,
          "loading"
        );

        const result = await processFolder(folder);

        newStatus[folder.id] = result.success ? "success" : "error";
        setProcessingStatus({ ...newStatus });

        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      const successMsg = `Successfully imported ${successCount} projects.`;
      const failMsg =
        failCount > 0 ? ` Failed to import ${failCount} projects.` : "";
      showCustomToast(successMsg + failMsg, "success", 5000);

      if (successCount > 0) {
        setTimeout(() => {
          navigate("/user-projects");
        }, 5000);
      }
    } catch (error) {
      console.error("Error in batch processing:", error);
      showCustomToast("Failed to complete batch import", "error");
    } finally {
      setProcessing(false);
    }
  };

  const refreshFolders = () => {
    if (isAuthorized) {
      fetchProjectFolders(currentFolderId);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        {showToast && (
          <Toast message={toastMessage} type={toastType} onClose={hideToast} />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-8 text-center border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Bulk Project Import from Google Drive
              </h1>
              <p className="text-gray-500">
                Import multiple projects from your Google Drive folders
              </p>
            </div>

            <div className="p-6">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">
                    Checking Google Drive authorization...
                  </p>
                </div>
              ) : !isAuthorized ? (
                <div className="text-center py-8">
                  <button
                    onClick={handleAuthClick}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Authorize Google Drive Access
                  </button>
                  <p className="mt-4 text-gray-600">
                    Please authorize access to your Google Drive to import
                    projects
                  </p>
                </div>
              ) : (
                <div>
                  {/* Breadcrumb Navigation */}
                  <div className="mb-4">
                    <nav className="flex" aria-label="Breadcrumb">
                      <ol className="inline-flex items-center space-x-1 md:space-x-3 flex-wrap">
                        {breadcrumbs.map((item, index) => (
                          <li
                            key={item.id}
                            className="inline-flex items-center"
                          >
                            {index > 0 && (
                              <span className="mx-2 text-gray-400">/</span>
                            )}
                            <button
                              onClick={() => navigateToBreadcrumb(index)}
                              className={`text-sm ${
                                index === breadcrumbs.length - 1
                                  ? "font-medium text-blue-600"
                                  : "text-gray-700 hover:text-blue-600"
                              }`}
                              disabled={isFetchingFolders}
                            >
                              {item.name}
                            </button>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  </div>

                  <div className="flex justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-medium text-gray-900">
                        Project Folders
                      </h2>
                      <p className="text-sm text-gray-500">
                        Select the project folders you want to import
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Current path: {currentFolderId}
                      </p>
                    </div>
                    <div>
                      <button
                        onClick={refreshFolders}
                        disabled={processing || isFetchingFolders}
                        className="mr-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        {isFetchingFolders ? "Loading..." : "Refresh"}
                      </button>
                      <button
                        onClick={toggleSelectAll}
                        disabled={
                          processing ||
                          projectFolders.length === 0 ||
                          isFetchingFolders
                        }
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        {selectedProjects.length === projectFolders.length
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    </div>
                  </div>

                  {debugInfo && (
                    <div className="mt-2 mb-4 bg-gray-100 p-2 rounded text-xs overflow-auto max-h-20">
                      <pre>{debugInfo}</pre>
                    </div>
                  )}

                  {isFetchingFolders ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="text-gray-600 mt-4">Loading folders...</p>
                    </div>
                  ) : projectFolders.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-600">
                        No folders found in this location.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Navigate to a different location or refresh the folder
                        list.
                      </p>
                      <button
                        onClick={refreshFolders}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Refresh Folder List
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <div className="overflow-y-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="w-12 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Select
                              </th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Folder Name
                              </th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {projectFolders.map((folder) => (
                              <tr key={folder.id} className="hover:bg-gray-50">
                                <td className="py-4 pl-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedProjects.includes(
                                      folder.id
                                    )}
                                    onChange={() =>
                                      toggleProjectSelection(folder.id)
                                    }
                                    disabled={processing || isFetchingFolders}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  <button
                                    onClick={() => navigateToFolder(folder)}
                                    className="text-blue-600 hover:text-blue-800"
                                    disabled={isFetchingFolders}
                                  >
                                    {folder.name}
                                  </button>
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {processingStatus[folder.id] ===
                                    "processing" && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      Processing...
                                    </span>
                                  )}
                                  {processingStatus[folder.id] ===
                                    "success" && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Success
                                    </span>
                                  )}
                                  {processingStatus[folder.id] === "error" && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {processing && (
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-blue-600 h-2.5 rounded-full"
                              style={{
                                width: `${
                                  (progress.current / progress.total) * 100
                                }%`,
                              }}
                            ></div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            Processing {progress.current} of {progress.total}{" "}
                            projects
                          </p>
                        </div>
                      )}

                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={processSelectedFolders}
                          disabled={
                            processing ||
                            selectedProjects.length === 0 ||
                            isFetchingFolders
                          }
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {processing
                            ? "Processing..."
                            : `Import ${selectedProjects.length} Selected Projects`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 bg-gray-50 rounded-lg p-6 mx-6 mb-6">
              <h2 className="text-sm font-medium text-gray-900 mb-4">
                Import Requirements
              </h2>
              <ul className="space-y-2">
                <li className="flex items-start text-sm text-gray-600">
                  <span className="mr-2">•</span>
                  Each project should be in its own folder in Google Drive
                </li>
                <li className="flex items-start text-sm text-gray-600">
                  <span className="mr-2">•</span>
                  Each folder must contain one Excel file (.xlsx) with project
                  data
                </li>
                <li className="flex items-start text-sm text-gray-600">
                  <span className="mr-2">•</span>
                  Optional: Include image files for the project
                </li>
                <li className="flex items-start text-sm text-gray-600">
                  <span className="mr-2">•</span>
                  Navigate through your Drive folders to find project folders
                </li>
                <li className="flex items-start text-sm text-gray-600">
                  <span className="mr-2">•</span>
                  The Excel file should follow the same format as for single
                  project upload
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default BulkProjectImporter;
